import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import { FETCH_T_PDCAS } from '../../queries/pdca_t_pdca.queries.type';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CellRendererTrend } from '../shared/agGrid/cellRenderer';
import { NIL } from 'uuid';
import { CellClickedEvent, iconSetQuartzLight, themeQuartz, ValueGetterParams } from 'ag-grid-community';
import { useLocation } from 'react-router-dom';
import { getSeisakuChainFromLeaf, getSeisakuLeaf } from '../../utils/keikakuFunctions';
import { FETCH_M_LG_CONFIG } from '@/main/queries/setting_m_lg_config.queries.type';

type Data = FETCH_T_PDCAS['pdca_t_pdca'][number] & {
    jigyoName: string | null;
    jigyohi: number | null | undefined;
    zaigenIppan: number | null | undefined;
    zaigenKen: number | null | undefined;
    zaigenKikin: number | null | undefined;
    zaigenKuni: number | null | undefined;
    zaigenChihosai: number | null | undefined;
    zaigenOther: number | null | undefined;
    preJigyohi: number | null | undefined;
    diffJigyohi: number | null | undefined;
    keikaku: FETCH_T_PDCAS['pdca_m_seisaku_shisaku'];
};

type JigyoAgGridReactProps = AgGridReactProps<Data>;

type HisroryState = {
    divisionId: string | null;
    seisakuId: string | null;
};

/**
 * 事業一覧Presenterのカスタムフック
 * @param jigyoYear
 * @param data
 * @param onNavigate
 * @returns
 */
export const useJigyoListPresenter = (
    jigyoYear: string,
    data: FETCH_T_PDCAS | undefined,
    lgConfigData: FETCH_M_LG_CONFIG | undefined,
    onNavigate: (jigyoID: string) => void
) => {
    const { state } = useLocation<HisroryState>();

    const gridRef = useRef<AgGridReact>(null);
    const gridColumnApi = useRef(null);

    const { divisionId, seisakuId } = state;

    const [selectedSoshikiDivision, setSelectedSoshikiDivision] = useState<typeof soshikiDivisionFilterList>([]);
    const [selectedSeisakuShisaku, setSelectedSeisakuShisaku] = useState<typeof seisakuShisakuFilterList>([]);

    const [firstDataRendered, setFirstDataRendered] = useState(false);
    const [keyWord, setKeyWord] = useState('');

    // 担当課選択時のイベントハンドラ
    const onSelectSoshikiDivision = useCallback(
        (selectedSoshikis: typeof soshikiDivisionFilterList) => {
            setSelectedSoshikiDivision(selectedSoshikis);
        },
        [setSelectedSoshikiDivision]
    );

    // 計画選択時のイベントハンドラ
    const onSelectSeisakuShisaku = useCallback(
        (selectedSeisakus: typeof seisakuShisakuFilterList) => {
            setSelectedSeisakuShisaku(selectedSeisakus);
        },
        [setSelectedSeisakuShisaku]
    );

    // AgGrid RowData
    const rowData = useMemo<JigyoAgGridReactProps['rowData']>(() => {
        if (!data) {
            return null;
        } else {
            let filteredData = data.pdca_t_pdca;
            // 担当課でフィルタリング
            if (selectedSoshikiDivision.length > 0) {
                filteredData = filteredData.filter((item) =>
                    selectedSoshikiDivision.some((soshiki) => soshiki.id === item.m_soshiki_division_id_main)
                );
            }
            // 政策でフィルタリング
            if (selectedSeisakuShisaku.length > 0) {
                // 葉の計画を平坦化
                const flatSeisakuShisaku = selectedSeisakuShisaku.flatMap((seisaku) => seisaku.children);
                filteredData = filteredData.filter((item) => {
                    // 事業に紐づく政策を取得
                    const seisakuShisakuLinkedJigyo = item.t_jigyo.c_jimu_jigyo_seisaku_shisakus;
                    // 事業の政策に選択した政策が含まれているか
                    return flatSeisakuShisaku.some((seisaku) => {
                        const jigyoSeisakuIds = seisakuShisakuLinkedJigyo.map((jigyoSeisaku) => jigyoSeisaku.m_seisaku_shisaku_id);
                        return jigyoSeisakuIds.includes(seisaku.id);
                    });
                });
            }

            // キーワードでフィルタリング
            if (keyWord !== '') {
                filteredData = filteredData.filter((item) => item.jigyo_gaiyo?.includes(keyWord) || item.jigyo_name?.includes(keyWord));
            }

            // 款項目で並び替え
            filteredData = [...filteredData].sort((a, b) => {
                const a_kaikei_name = a.t_jigyo.m_kaikei?.kaikei_name;
                const b_kaikei_name = b.t_jigyo.m_kaikei?.kaikei_name;

                if (isNaN(Number(a.t_jigyo.yosan_kan_cd))) return 1;
                if (isNaN(Number(b.t_jigyo.yosan_kan_cd))) return -1;
                if (isNaN(Number(a.t_jigyo.yosan_kou_cd))) return 1;
                if (isNaN(Number(b.t_jigyo.yosan_kou_cd))) return -1;
                if (isNaN(Number(a.t_jigyo.yosan_moku_cd))) return 1;
                if (isNaN(Number(b.t_jigyo.yosan_moku_cd))) return -1;
                if (a.m_soshiki_division && b.m_soshiki_division) {
                    if (isNaN(a.m_soshiki_division.division_cd)) return 1;
                    if (isNaN(b.m_soshiki_division.division_cd)) return -1;
                }
                if (isNaN(Number(a.t_jigyo.jigyo_hyoji_num))) return 1;
                if (isNaN(Number(b.t_jigyo.jigyo_hyoji_num))) return -1;

                //款＞項＞目＞組織＞表示順の順で複数条件で並び替え
                if (a_kaikei_name && b_kaikei_name) {
                    const a_kaikei_cd = a_kaikei_name === '一般会計' ? 1 : 2;
                    const b_kaikei_cd = b_kaikei_name === '一般会計' ? 1 : 2;

                    if (Number(a_kaikei_cd) > Number(b_kaikei_cd)) return 1; //会計名
                    if (Number(a_kaikei_cd) < Number(b_kaikei_cd)) return -1;
                }
                if (Number(a.t_jigyo.yosan_kan_cd) > Number(b.t_jigyo.yosan_kan_cd)) return 1; //款
                if (Number(a.t_jigyo.yosan_kan_cd) < Number(b.t_jigyo.yosan_kan_cd)) return -1;
                if (Number(a.t_jigyo.yosan_kou_cd) > Number(b.t_jigyo.yosan_kou_cd)) return 1; //項
                if (Number(a.t_jigyo.yosan_kou_cd) < Number(b.t_jigyo.yosan_kou_cd)) return -1;
                if (Number(a.t_jigyo.yosan_moku_cd) > Number(b.t_jigyo.yosan_moku_cd)) return 1; //目
                if (Number(a.t_jigyo.yosan_moku_cd) < Number(b.t_jigyo.yosan_moku_cd)) return -1;
                if (a.m_soshiki_division && b.m_soshiki_division) {
                    if (Number(a.m_soshiki_division.division_cd) > Number(b.m_soshiki_division.division_cd)) return 1; //担当課
                    if (Number(a.m_soshiki_division.division_cd) < Number(b.m_soshiki_division.division_cd)) return -1;
                }

                if (Number(a.t_jigyo.jigyo_hyoji_num) > Number(b.t_jigyo.jigyo_hyoji_num)) return 1; //表示順
                if (Number(a.t_jigyo.jigyo_hyoji_num) < Number(b.t_jigyo.jigyo_hyoji_num)) return -1;

                return 0;
            });
            // }
            return filteredData.map((item) => {
                // 今年度判定
                // 当年度(created_year)は予算、前年度(created_year - 1)は決算確定フラグ(is_kessan_confirmed)が
                // trueになるまで予算のまま(9月頃に決算へ切り替わる)。jigyoList.container.hooksの判定と揃えること。
                const lgConfig = lgConfigData?.setting_m_lg_config[0];
                const year = Number(jigyoYear);
                const isYosan =
                    year === lgConfig?.created_year || (year === (lgConfig?.created_year ?? NaN) - 1 && !lgConfig?.is_kessan_confirmed);
                const currentDataList = isYosan ? data.pdca_t_jigyohi : data.pdca_t_kessan;

                let tJigyohi;

                // 前年度事業費
                const preTJigyohi = data.pdca_t_jigyohi.find(
                    (jigyohi) =>
                        jigyohi.jigyo_year === Number(jigyoYear) - 1 &&
                        jigyohi.m_jisshi_keikaku_id === item.t_jigyo.m_jisshi_keikaku.id &&
                        jigyohi.process_cd === 30
                );

                // 前年度事業費
                const preTKessan = data.pdca_t_kessan.find(
                    (kessan) =>
                        kessan.jigyo_year === Number(jigyoYear) - 1 && kessan.m_jisshi_keikaku_id === item.t_jigyo.m_jisshi_keikaku.id
                );
                const preJigyohi = preTJigyohi ? preTJigyohi.jigyohi : preTKessan ? preTKessan.jigyohi : null;

                if (isYosan) {
                    // 今年度は pdca_t_jigyohi から取得（process_cd 条件あり）
                    tJigyohi = data.pdca_t_jigyohi.find(
                        (row) =>
                            row.jigyo_year === Number(jigyoYear) &&
                            row.m_jisshi_keikaku_id === item.t_jigyo.m_jisshi_keikaku.id &&
                            row.process_cd === 30
                    );
                } else {
                    // 昨年度は pdca_t_kessan から取得（process_cd 条件なし）
                    // tJigyohi = data.pdca_t_kessan.find(
                    //     (row) => row.jigyo_year === Number(jigyoYear) && row.m_jisshi_keikaku_id === item.t_jigyo.m_jisshi_keikaku.id
                    // );
                    // 前年度は pdca_t_kessan から取得（合算）
                    tJigyohi = data.pdca_t_kessan
                        ?.filter(
                            (row) => row.jigyo_year === Number(jigyoYear) && row.m_jisshi_keikaku_id === item.t_jigyo.m_jisshi_keikaku.id
                        )
                        ?.reduce((acc, cur) => {
                            if (!acc) return { ...cur };
                            return {
                                ...acc,
                                jigyohi: (acc.jigyohi ?? 0) + (cur.jigyohi ?? 0),
                                zaigen_kuni: (acc.zaigen_kuni ?? 0) + (cur.zaigen_kuni ?? 0),
                                zaigen_chihosai: (acc.zaigen_chihosai ?? 0) + (cur.zaigen_chihosai ?? 0),
                                zaigen_ippan: (acc.zaigen_ippan ?? 0) + (cur.zaigen_ippan ?? 0),
                                zaigen_ken: (acc.zaigen_ken ?? 0) + (cur.zaigen_ken ?? 0),
                                zaigen_kikin: (acc.zaigen_kikin ?? 0) + (cur.zaigen_kikin ?? 0),
                                zaigen_kurikoshi: (acc.zaigen_kurikoshi ?? 0) + (cur.zaigen_kurikoshi ?? 0),
                                zaigen_other: (acc.zaigen_other ?? 0) + (cur.zaigen_other ?? 0),
                            };
                        }, null as null | (typeof data.pdca_t_kessan)[number]);
                }

                const jigyohi = tJigyohi?.jigyohi;
                // 事業費差分
                const diffJigyohi = jigyohi && preJigyohi ? jigyohi - preJigyohi : null;

                //財源
                const zaigenChihosai = tJigyohi?.zaigen_chihosai;
                const zaigenIppan = tJigyohi?.zaigen_ippan;
                const zaigenKen = tJigyohi?.zaigen_ken;
                const zaigenKikin = tJigyohi?.zaigen_kikin;
                const zaigenKuni = tJigyohi?.zaigen_kuni;
                const zaigenOther = tJigyohi?.zaigen_other;

                // 計画
                let keikaku: FETCH_T_PDCAS['pdca_m_seisaku_shisaku'] = [];
                const targetKeikaku = data.pdca_m_keikaku.find((keikaku) => keikaku.keikaku_name === '総合計画');
                if (targetKeikaku) {
                    // 総合計画を絞り込み
                    const filteredKeikakuData = data.pdca_m_seisaku_shisaku.filter(
                        (seisakuShisaku) => seisakuShisaku.m_keikaku_id === targetKeikaku.id
                    );
                    // 総合計画の紐づけを１つ取得
                    const targetCSeisaku = item.t_jigyo.c_jimu_jigyo_seisaku_shisakus.find((seisaku) => {
                        return filteredKeikakuData.some((filteredSeisaku) => filteredSeisaku.id === seisaku.m_seisaku_shisaku_id);
                    });
                    // 紐づけがある場合は計画の階層を取得
                    if (targetCSeisaku) {
                        const targetMSeisaku = filteredKeikakuData.find((seisaku) => seisaku.id === targetCSeisaku.m_seisaku_shisaku_id);
                        if (targetMSeisaku) {
                            keikaku = getSeisakuChainFromLeaf(targetMSeisaku, filteredKeikakuData);
                        }
                    }
                }
                return {
                    ...item,
                    jigyoName: item.jigyo_name,
                    jigyohi,
                    preJigyohi: preJigyohi,
                    diffJigyohi,
                    keikaku,
                    zaigenIppan,
                    zaigenKen,
                    zaigenKikin,
                    zaigenKuni,
                    zaigenChihosai,
                    zaigenOther,
                };
            });
        }
    }, [data, jigyoYear, selectedSoshikiDivision, selectedSeisakuShisaku, keyWord]);

    const valueGetterYosanKubun = (params: ValueGetterParams<Data>, kubunName: 'kan' | 'kou' | 'moku') => {
        const targetName = params.data?.t_jigyo[`yosan_${kubunName}`] ?? '';
        const targetNum = params.data?.t_jigyo[`yosan_${kubunName}_cd`];
        return `${targetNum} ${targetName}`;
    };

    // AgGrid カラム定義
    const columnDefs = useMemo<JigyoAgGridReactProps['columnDefs']>(() => {
        const screenWidth = window.innerWidth;

        return [
            { headerName: '事業番号', field: 't_jigyo.keikaku_jigyo_cd', hide: true },
            { headerName: '事業名', field: 'jigyoName', flex: 4, suppressMovable: true },
            {
                headerName: '事業区分',
                field: 't_jigyo.new_or_continue',
                valueGetter: (params) => {
                    return params.data?.t_jigyo.new_or_continue === 1 ? '新規' : '継続';
                },
                hide: true,
            },
            {
                headerName: '担当課',
                field: 'm_soshiki_division_id_main',
                valueGetter: (params) => {
                    const targetDivision = data?.pdca_m_soshiki_division.find(
                        (division) => division.id === params.data?.m_soshiki_division_id_main
                    );
                    return targetDivision?.division_name;
                },
                flex: 1,
                hide: screenWidth < 600 ? true : false,
            },
            {
                headerName: '担当係',
                field: 'tantou_kakari_main',
                hide: true,
            },
            {
                headerName: '会計',
                field: 't_jigyo.m_kaikei.kaikei_name',
                hide: screenWidth < 600 ? true : false,
            },
            {
                headerName: '予算区分',
                hide: true,
                children: [
                    {
                        headerName: '款',
                        valueGetter: (params) => valueGetterYosanKubun(params, 'kan'),
                        hide: true,
                    },
                    {
                        headerName: '項',
                        valueGetter: (params) => valueGetterYosanKubun(params, 'kou'),
                        hide: true,
                    },
                    {
                        headerName: '目',
                        valueGetter: (params) => valueGetterYosanKubun(params, 'moku'),
                        hide: true,
                    },
                ],
            },

            {
                headerName: 'まちづくりビジョン',
                hide: true,
                children: [
                    {
                        headerName: '政策',
                        field: 'keikaku.0.seisaku_shisaku_name',
                        hide: true,
                    },
                    {
                        headerName: '施策',
                        field: 'keikaku.1.seisaku_shisaku_name',
                        hide: true,
                    },
                ],
            },
            {
                headerName: '前年度予算額',
                field: 'preJigyohi',
                cellStyle: { textAlign: 'right' },
                valueFormatter: (params) => params.value?.toLocaleString(),
                hide: true,
            },
            {
                headerName: '前年比(千円)',
                field: 'diffJigyohi',
                flex: 1,
                cellStyle: { textAlign: 'right' },
                suppressMovable: true,
                cellRenderer: CellRendererTrend,
            },
            {
                headerName: '事業費(千円)',
                field: 'jigyohi',
                flex: 1,
                cellStyle: { textAlign: 'right' },
                suppressMovable: true,
                valueFormatter: (params) => params.value?.toLocaleString(),
            },
            {
                headerName: '一般財源(千円)',
                field: 'zaigenIppan',
                hide: true,
                valueFormatter: (params) => params.value?.toLocaleString(),
            },
            {
                headerName: '国庫支出金(千円)',
                field: 'zaigenKuni',
                hide: true,
                valueFormatter: (params) => params.value?.toLocaleString(),
            },
            {
                headerName: '県支出金(千円)',
                field: 'zaigenKen',
                hide: true,
                valueFormatter: (params) => params.value?.toLocaleString(),
            },
            {
                headerName: '基金(千円)',
                field: 'zaigenKikin',
                hide: true,
                valueFormatter: (params) => params.value?.toLocaleString(),
            },
            {
                headerName: '地方債(千円)',
                field: 'zaigenChihosai',
                hide: true,
                valueFormatter: (params) => params.value?.toLocaleString(),
            },
            {
                headerName: 'その他財源(千円)',
                field: 'zaigenOther',
                hide: true,
                valueFormatter: (params) => params.value?.toLocaleString(),
            },
        ];
    }, [data]);

    // AgGrid カスタムスタイル
    const theme: JigyoAgGridReactProps['theme'] = useMemo(() => {
        return themeQuartz.withPart(iconSetQuartzLight).withParams({
            backgroundColor: '#ffffff',
            browserColorScheme: 'light',
            columnBorder: false,
            fontFamily: 'Arial',
            foregroundColor: 'rgb(46, 55, 66)',
            headerBackgroundColor: '#71A9C7',
            headerFontSize: 14,
            headerFontWeight: 400,
            headerTextColor: '#ffffff',
            oddRowBackgroundColor: '#F9FAFB',
            rowBorder: false,
            sidePanelBorder: false,
            spacing: 8,
            wrapperBorder: false,
            wrapperBorderRadius: 8,
        });
    }, []);

    // AgGrid 日本語化
    const localeText: JigyoAgGridReactProps['localeText'] = useMemo(() => {
        return {
            // ページネーション関連
            page: '',
            more: 'もっと見る',
            to: '～',
            of: '/',
            nextPage: '次のページ',
            lastPage: '最後のページ',
            firstPage: '最初のページ',
            previousPage: '前のページ',
            loadingOoo: '読み込み中...',
            noRowsToShow: '表示するデータがありません',
            // フィルター関連
            filterOoo: 'フィルター...',
            equals: '等しい',
            notEqual: '等しくない',
            contains: '含む',
            notContains: '含まない',
            startsWith: 'で始まる',
            endsWith: 'で終わる',
            lessThan: '未満',
            lessThanOrEqual: '以下',
            greaterThan: 'より大きい',
            greaterThanOrEqual: '以上',
            inRange: '範囲内',
            blank: '空白',
            notBlank: '空白でない',
        };
    }, []);

    // AgGrid セルクリック時のイベントハンドラ
    const onCellClicked: JigyoAgGridReactProps['onCellClicked'] = useCallback(
        (params: CellClickedEvent) => {
            let jigyoID = NIL;

            if (params.data) {
                jigyoID = params.data.t_jigyo.m_jisshi_keikaku.id;
            }
            onNavigate(jigyoID);
        },
        [onNavigate]
    );

    //AgGrid GridOptions
    const gridOptions: JigyoAgGridReactProps['gridOptions'] = useMemo(() => {
        return {
            defaultColDef: {
                editable: false,
                filter: true,
                sortable: true,
                flex: 1,
                minWidth: 130,
            },
            paginationAutoPageSize: true,
            pagination: false,
            getRowId: (params) => params.data.id,
            groupHeaderHeight: 0,
        };
    }, []);

    const soshikiDivisionFilterList: (FETCH_T_PDCAS['pdca_m_soshiki_division'][number] & { count: number })[] = useMemo(() => {
        if (!data) {
            return [];
        }
        const soshikiDivisionList = data.pdca_m_soshiki_division;
        // 事業件数を取得
        const soshikiDivisionListWithCount = soshikiDivisionList.map((division) => {
            const count: number = rowData?.filter((item: Data) => item.m_soshiki_division_id_main === division.id).length ?? 0;
            return {
                ...division,
                count,
            };
        });
        if (selectedSeisakuShisaku.length > 0) {
            return soshikiDivisionListWithCount.filter((division) => division.count > 0);
        } else {
            return soshikiDivisionListWithCount;
        }
    }, [data, rowData, selectedSeisakuShisaku]);

    const seisakuShisakuFilterList: (FETCH_T_PDCAS['pdca_m_seisaku_shisaku'][number] & {
        count: number;
        children: FETCH_T_PDCAS['pdca_m_seisaku_shisaku'];
    })[] = useMemo(() => {
        if (!data) {
            return [];
        } else {
            const targetKeikaku = data.pdca_m_keikaku.find((keikaku) => keikaku.keikaku_name === '総合計画');
            if (!targetKeikaku) {
                return [];
            }
            // 計画を絞り込み
            const filteredKeikakuData = data.pdca_m_seisaku_shisaku.filter(
                (seisakuShisaku) => seisakuShisaku.m_keikaku_id === targetKeikaku.id
            );
            // 最上位施策(根)を取得
            const topLevelShisaku = filteredKeikakuData.filter((seisakuShisaku) => !seisakuShisaku.parent_cd);

            const withCountList = topLevelShisaku.map((seisaku) => {
                const childrenSeisakus = getSeisakuLeaf(seisaku, filteredKeikakuData);
                // 事業数を取得
                const jigyoDatas = rowData?.filter((item) => {
                    // 事業に紐づく政策を取得
                    const seisakuShisakuLinkedJigyo = item.t_jigyo.c_jimu_jigyo_seisaku_shisakus;
                    // 事業の政策に選択した政策が含まれているか
                    return childrenSeisakus.some((seisaku) => {
                        const jigyoSeisakuIds = seisakuShisakuLinkedJigyo.map((jigyoSeisaku) => jigyoSeisaku.m_seisaku_shisaku_id);
                        return jigyoSeisakuIds.includes(seisaku.id);
                    });
                });
                return {
                    ...seisaku,
                    count: jigyoDatas?.length ?? 0,
                    children: childrenSeisakus,
                };
            });
            if (selectedSoshikiDivision.length > 0) {
                return withCountList.filter((seisaku) => seisaku.count > 0);
            } else {
                return withCountList;
            }
        }
    }, [data, rowData, selectedSoshikiDivision]);

    // CSV出力ボタンクリック時のイベントハンドラ
    const handleClickExportCSV = useCallback(() => {
        //GAイベント計測
        // GAイベント送信（型エラーが出る場合は下の宣言を追加）
        (window as any).gtag('event', 'csv_export', {
            event_category: '事業一覧',
            event_label: jigyoYear,
        });
        gridRef.current?.api.exportDataAsCsv({ allColumns: true, fileName: '事業一覧.csv' });
    }, [gridRef]);

    // 初回データレンダリング時の処理
    useEffect(() => {
        if (rowData && !firstDataRendered) {
            setFirstDataRendered(true);
        }
    }, [rowData, firstDataRendered]);

    // 初期表示時の担当課選択
    useEffect(() => {
        if (divisionId && firstDataRendered) {
            setSelectedSoshikiDivision(soshikiDivisionFilterList.filter((division) => division.id === divisionId));
        }
    }, [divisionId, firstDataRendered]);

    useEffect(() => {
        if (seisakuId && firstDataRendered) {
            setSelectedSeisakuShisaku(seisakuShisakuFilterList.filter((seisaku) => seisaku.id === seisakuId));
        }
    }, [seisakuId, firstDataRendered]);

    return {
        jigyoListGridProps: { ref: gridRef, rowData, columnDefs, localeText, onCellClicked, gridOptions, theme },
        divisionMultiSelectProps: {
            items: soshikiDivisionFilterList,
            selectedItems: selectedSoshikiDivision,
            onSelect: onSelectSoshikiDivision,
        },
        seisakuMultiSelectProps: {
            items: seisakuShisakuFilterList,
            selectedItems: selectedSeisakuShisaku,
            onSelect: onSelectSeisakuShisaku,
        },
        keywordSearchInputProps: {
            className: 'mb-4',
            type: 'text',
            placeholder: 'キーワード検索',
            onChange: (e: any) => setKeyWord(e.target.value),
        },
        exportCSVButtonProps: { onClick: handleClickExportCSV },
    };
};
